/**
 * Nach dem Wechsel der kampagnenweiten Schrift: Elemente mit eigener Schrift finden und auf
 * Wunsch auf die neue Schrift umstellen.
 *
 * Eine eigene Schrift kann aus dem Stil-Tab kommen, aus dem CSS einer Vorlage oder aus einem
 * KI-Block. Statt jede dieser Quellen einzeln zu kennen, wird geprüft, was am Ende angezeigt wird:
 * Der Server liefert dieselben Schriftregeln wie für die Seite, nur mit zwei Schriften, die es
 * nicht gibt (KampagnenViewService#sondenCss). Wer danach eine davon trägt, folgt der
 * Kampagnenschrift, alle anderen haben eine eigene.
 */

// Gleiche Namen wie KampagnenViewService.SONDE_UEBERSCHRIFT / SONDE_TEXT
const SONDE = { ueberschrift: "CsSondeUeberschrift", text: "CsSondeText" };
const VARIABLE = { ueberschrift: "--cs-schrift-ueberschrift", text: "--cs-schrift-text" };
const UEBERSCHRIFTEN = "h1,h2,h3,h4,h5,h6";
const OHNE_TEXT = "script,style,noscript,template,svg,title,newsection,[data-vvveb-helpers]";

let sondenCss = null;

const ersteFamilie = (wert) =>
  (wert || "")
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "");

async function ladeSonde(config) {
  if (sondenCss === null) {
    const response = await fetch(`${config.schriftenCssUrl}?sonde=true`);
    if (!response.ok) throw new Error("Schriftprobe konnte nicht geladen werden");
    sondenCss = await response.text();
  }
  return sondenCss;
}

/**
 * Elemente mit eigenem Text: Nur dort ist eine Schrift zu sehen. Icons (leere <i>) fallen damit
 * von selbst heraus und behalten ihre Icon-Schrift.
 */
function kandidaten(doc, art) {
  if (!doc?.body) return [];
  return [...doc.body.querySelectorAll("*")].filter((element) => {
    if (element.closest(OHNE_TEXT)) return false;
    const hatText = [...element.childNodes].some(
      (knoten) => knoten.nodeType === Node.TEXT_NODE && knoten.textContent.trim(),
    );
    if (!hatText) return false;
    return (element.closest(UEBERSCHRIFTEN) !== null) === (art === "ueberschrift");
  });
}

/** Legt die Probe über die Seite, ruft prüfen auf und stellt den alten Zustand wieder her. */
function mitSonde(doc, css, pruefen) {
  const echt = [...doc.querySelectorAll("#cs-schriften")];
  echt.forEach((element) => (element.disabled = true));
  const sonde = doc.createElement("style");
  sonde.setAttribute("data-vvveb-helpers", "");
  sonde.textContent = css;
  // ans Ende wie beim Ausliefern, wo die Schriften als Letztes im <head> stehen
  doc.head.append(sonde);
  try {
    return pruefen();
  } finally {
    sonde.remove();
    echt.forEach((element) => (element.disabled = false));
  }
}

/**
 * Folgt der Kampagnenschrift. Text darf auch der Überschriftenschrift folgen: KI-Seiten setzen
 * Überschriften oft als <p class="titel"> mit der Variable der Überschriften.
 */
function folgt(element, art) {
  const ansicht = element.ownerDocument.defaultView;
  const familie = ersteFamilie(ansicht.getComputedStyle(element).fontFamily);
  return familie === SONDE[art] || (art === "text" && familie === SONDE.ueberschrift);
}

function analysieren(doc, art, name, css) {
  const ansicht = doc.defaultView;
  const elemente = kandidaten(doc, art);
  const bisher = new Map(
    elemente.map((element) => [element, ansicht.getComputedStyle(element).fontFamily]),
  );
  const eigene = mitSonde(doc, css, () => elemente.filter((element) => !folgt(element, art)));
  // Gezählt wird nur, was gleich anders aussieht; eine eigene Schrift, die zufällig der neuen
  // entspricht, fällt nicht auf
  const sichtbar = eigene.filter((element) => ersteFamilie(bisher.get(element)) !== name);
  return { eigene, sichtbar, bisher };
}

/**
 * Bringt die Elemente auf die Kampagnenschrift: zuerst die Schrift aus Stil-Tab und style-Attribut
 * entfernen; was dann noch abweicht, hängt an einer Regel der Vorlage und wird über die Variable
 * an die Kampagnenschrift gebunden. Mit der bisherigen Schrift als Rückfall, falls die
 * Kampagnenschrift später wieder geleert wird.
 */
function angleichen(doc, analyse, art, css, entfernen) {
  const { eigene, bisher } = analyse;
  entfernen(eigene);

  const variabel = (element) => `var(${VARIABLE[art]}, ${bisher.get(element)})`;
  const rest = mitSonde(doc, css, () => eigene.filter((element) => !folgt(element, art)));
  rest.forEach((element) => element.style.setProperty("font-family", variabel(element)));

  // Regeln mit !important lassen sich nur mit !important überstimmen
  const hartnaeckig = mitSonde(doc, css, () => rest.filter((element) => !folgt(element, art)));
  hartnaeckig.forEach((element) =>
    element.style.setProperty("font-family", variabel(element), "important"),
  );
}

/** Wie Vvveb.StyleManager.removeStyleFrom, aber für ein Dokument außerhalb des Editors. */
function editorSchriftEntfernen(doc, elemente) {
  elemente.forEach((element) => element.style.removeProperty("font-family"));

  const stil = doc.getElementById("vvvebjs-styles");
  if (!stil?.sheet) return;

  const trifft = (selektor) => {
    try {
      return [...doc.querySelectorAll(selektor.replace(/:{1,2}[a-z-]+(\([^)]*\))?$/i, ""))].some(
        (element) => elemente.includes(element),
      );
    } catch {
      return false;
    }
  };
  // rückwärts, weil leer gewordene Regeln gleich entfernt werden
  const durchgehen = (liste) => {
    for (let i = liste.cssRules.length - 1; i >= 0; i--) {
      const regel = liste.cssRules[i];
      if (regel.cssRules && !regel.selectorText) {
        durchgehen(regel);
        if (regel.cssRules.length === 0) liste.deleteRule(i);
      } else if (regel.style?.getPropertyValue("font-family") && trifft(regel.selectorText)) {
        regel.style.removeProperty("font-family");
        if (regel.style.length === 0) liste.deleteRule(i);
      }
    }
  };
  durchgehen(stil.sheet);
  stil.textContent = [...stil.sheet.cssRules].map((regel) => regel.cssText).join("\n");
}

/**
 * Die Seite, die gerade nicht im Editor offen ist, ohne Skripte laden: Sie dürfen das Markup
 * nicht verändern, das danach gespeichert wird. Die Stylesheets sind dieselben wie im Editor.
 */
async function seiteImHintergrund(url, config) {
  const response = await fetch(url);
  if (!response.ok) return null;
  let html = await response.text();

  const hilfe = (link) => `<link data-vvveb-helpers rel="stylesheet" ${link}>`;
  html = html
    .replace(/<head[^>]*>/i, (kopf) => kopf + hilfe('href="/css/main.css"'))
    .replace(/<\/head>/i, hilfe(`id="cs-schriften" href="${config.schriftenCssUrl}"`) + "</head>");

  const rahmen = document.createElement("iframe");
  rahmen.setAttribute("sandbox", "allow-same-origin");
  rahmen.setAttribute("aria-hidden", "true");
  rahmen.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1280px;height:900px;visibility:hidden;";
  const geladen = new Promise((resolve) => {
    rahmen.addEventListener("load", resolve, { once: true });
    // eine hängende Schrift von Google darf das Angleichen nicht blockieren
    setTimeout(resolve, 8000);
  });
  rahmen.srcdoc = html;
  document.body.append(rahmen);
  await geladen;
  return rahmen;
}

function alsHtml(doc) {
  const typ = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>\n` : "";
  return Vvveb.Builder.removeHelpers(typ + doc.documentElement.outerHTML, true);
}

function zusammenfassen(analysen, art) {
  const elemente = analysen.flatMap((analyse) => analyse.sichtbar);
  const anzahl =
    art === "ueberschrift"
      ? new Set(elemente.map((element) => element.closest(UEBERSCHRIFTEN))).size
      : elemente.length;

  const schriften = new Map();
  for (const analyse of analysen) {
    for (const element of analyse.sichtbar) {
      const familie = ersteFamilie(analyse.bisher.get(element)) || "Standard";
      schriften.set(familie, (schriften.get(familie) || 0) + 1);
    }
  }
  return { anzahl, schriften: [...schriften.keys()] };
}

const escape = (text) =>
  String(text).replace(/[&<>"']/g, (zeichen) => `&#${zeichen.charCodeAt(0)};`);

/**
 * Fragt nach, wenn Elemente eine eigene Schrift behalten würden, und stellt sie auf Wunsch um.
 *
 * @param art "ueberschrift" oder "text"
 * @param name die neue Kampagnenschrift
 * @param seiten { offen: "main"|"danke", urls: { main, danke } }
 * @param speichern (html, typ) => Promise für die Seite, die nicht im Editor offen ist
 * @returns ob nachgefragt wurde; sonst steht noch keine Meldung auf dem Bildschirm
 */
export async function schriftenAngleichen({ art, name, config, seiten, speichern }) {
  const editor = window.FrameDocument;
  if (!name || !editor || typeof cs === "undefined") return false;

  const css = await ladeSonde(config);
  const andere = seiten.offen === "danke" ? "main" : "danke";
  const rahmen = await seiteImHintergrund(seiten.urls[andere], config).catch(() => null);

  try {
    const imEditor = analysieren(editor, art, name, css);
    const imHintergrund = rahmen?.contentDocument
      ? analysieren(rahmen.contentDocument, art, name, css)
      : null;

    const { anzahl, schriften } = zusammenfassen([imEditor, imHintergrund].filter(Boolean), art);
    if (anzahl === 0) return false;

    const was =
      art === "ueberschrift"
        ? anzahl === 1
          ? "1 Überschrift hat"
          : `${anzahl} Überschriften haben`
        : anzahl === 1
          ? "1 Textstelle hat"
          : `${anzahl} Textstellen haben`;

    const antwort = await cs.Swal.fire({
      icon: "question",
      title: "Eigene Schriftarten gefunden",
      html:
        `${was} eine eigene Schriftart (${schriften.map(escape).join(", ")}), aus dem Stil-Tab ` +
        `oder aus der Vorlage. Sollen sie auch <strong>${escape(name)}</strong> bekommen?`,
      showDenyButton: true,
      confirmButtonText: "Alle umstellen",
      denyButtonText: "Nur die ohne eigene Schriftart",
      customClass: { confirmButton: "btn btn-primary me-3", denyButton: "btn btn-secondary" },
      buttonsStyling: false,
    });
    if (!antwort.isConfirmed) {
      cs.notifySuccess("Schriftarten gespeichert, eigene Schriftarten bleiben.", "Schriftarten", {
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        backdrop: false,
        timer: 2500,
      });
      return true;
    }

    const imEditorGeaendert = imEditor.eigene.length > 0;
    if (imEditorGeaendert) editorAngleichen(editor, imEditor, art, css);

    // Die andere Seite ist nicht offen und wird deshalb gleich gespeichert
    let andereGespeichert = false;
    if (imHintergrund?.eigene.length) {
      const doc = rahmen.contentDocument;
      angleichen(doc, imHintergrund, art, css, (elemente) => editorSchriftEntfernen(doc, elemente));
      andereGespeichert = await speichern(alsHtml(doc), andere);
    }

    const offeneSeite = seiten.offen === "danke" ? "Dankeseite" : "Landingpage";
    const andereSeite = andere === "danke" ? "Dankeseite" : "Landingpage";
    const hinweise = [];
    if (imEditorGeaendert) hinweise.push(`Bitte die ${offeneSeite} noch speichern.`);
    if (andereGespeichert) hinweise.push(`Die ${andereSeite} ist bereits gespeichert.`);
    if (imHintergrund?.eigene.length && !andereGespeichert) {
      hinweise.push(`Die ${andereSeite} konnte nicht gespeichert werden.`);
    }
    cs.notifySuccess(["Umgestellt.", ...hinweise].join(" "), "Schriftarten");
    return true;
  } finally {
    rahmen?.remove();
  }
}

/**
 * Das Element, an dem im Stil-Tab „Für die ganze Seite übernehmen“ gewählt wurde, folgt danach der
 * Kampagnenschrift — auch wenn seine Schrift aus der Vorlage kommt und nicht aus dem Stil-Tab.
 */
export async function elementAngleichen({ element, art, config }) {
  const doc = element?.ownerDocument;
  if (!doc?.defaultView) return;

  const css = await ladeSonde(config);
  const bisher = new Map([[element, doc.defaultView.getComputedStyle(element).fontFamily]]);
  const eigene = mitSonde(doc, css, () => (folgt(element, art) ? [] : [element]));
  if (eigene.length) editorAngleichen(doc, { eigene, bisher }, art, css);
}

/** Im Editor über den StyleManager und rückgängig machbar, gespeichert wird mit der Seite. */
function editorAngleichen(editor, analyse, art, css) {
  const styleManager = Vvveb.StyleManager;
  const alteRegeln = styleManager.getCss();
  const alteStile = new Map(
    analyse.eigene.map((element) => [element, element.getAttribute("style")]),
  );

  angleichen(editor, analyse, art, css, (elemente) =>
    styleManager.removeStyleFrom(elemente, "font-family"),
  );

  Vvveb.Undo.addMutation({
    type: "style",
    target: editor.body,
    attributeName: "style",
    oldValue: alteRegeln,
    newValue: styleManager.getCss(),
  });
  for (const [element, alt] of alteStile) {
    const neu = element.getAttribute("style");
    if (neu !== alt) {
      Vvveb.Undo.addMutation({
        type: "attributes",
        target: element,
        attributeName: "style",
        oldValue: alt,
        newValue: neu,
      });
    }
  }
  Vvveb.Builder.reloadComponent?.();
}
